import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import LoadingIcon from 'assets/images/onboarding/step-loader.gif';
import Typography from 'components/Atoms/Typography/index';
import { Icon } from 'components/Icons';
import { sendAmplitudeEvents } from 'utils/common';
import { EventNames } from 'utils/events';

import './index.css';

let SelectedLanguage = '';
let SelectedTestingFramework = '';
let SelectedFramework = '';

const STEP_DONE = 'done';
const STEP_INITIATED = 'initiated';
const STEP_UNINITIATED = 'uninitiated';
const STEP_FAILED = 'failed';

const WAIT_TIME_FOR_TASK_COMPLETION_FIRST = 30000;
const WAIT_TIME_FOR_TASK_COMPLETION_SECOND = 30000;
const WAIT_TIME_FOR_TASK_COMPLETION_FINAL = 120000;

function handleCustomerSupportClick() {
  document.querySelector('.zsiq_custommain')?.click();
  handleAmplitudeEvents(EventNames.HYP_ONBOARDING_CUSTOMER_SUPPORT_QUICK_RUN);
}

function handleAmplitudeEvents(eventName = '', data = {}) {
  sendAmplitudeEvents(eventName, {
    ...data,
    'testing framework': SelectedTestingFramework,
    language: SelectedLanguage,
    framework: SelectedFramework,
  });
}

function getFinalFailureMessage() {
  return (
    <div>
      Looks like something went wrong. We are working on this. Meanwhile, please
      contact our{' '}
      <span
        className="quick-run-customer-support"
        onClick={handleCustomerSupportClick}
        tabIndex="0"
        role="button"
        onKeyDown={() => {}}
      >
        Customer Support
      </span>{' '}
      for assistance.
    </div>
  );
}

const TestSteps = [
  {
    description: 'Initiating Hyperexecute CLI',
    status: STEP_UNINITIATED,
    iconName: 'check-bold',
  },
  {
    description: 'Invoking Test Execution Command',
    status: STEP_UNINITIATED,
    iconName: 'check-bold',
  },
  {
    description: 'Discovering your Test cases',
    status: STEP_UNINITIATED,
    iconName: 'check-bold',
  },
  {
    description: 'Test cases triggered succesfully',
    status: STEP_UNINITIATED,
    iconName: 'check-bold',
  },
];

const TestStatusSteps = function ({ handleAllStepsCompleted }) {
  const currentStepState = useSelector(
    (state) =>
      state.onboarding?.currentConfigurationQuickRun?.triggerTestStatus
        ?.currentStep
  );

  const currentConfiguration = useSelector(
    (state) => state.onboarding?.currentConfigurationQuickRun
  );

  let timerOne = '';
  let timerTwo = '';
  let timerThree = '';

  const [testCompleted, setTestCompleted] = useState(false);
  const [steps, setSteps] = useState(structuredClone(TestSteps));
  const [errorViewed, setErrorViewed] = useState([]);

  const isCompletedRef = useRef(testCompleted);
  const failureHanderStep = useRef(1);
  useEffect(() => {
    SelectedFramework = currentConfiguration?.framework?.name;
    SelectedLanguage = currentConfiguration?.framework?.language;
    SelectedTestingFramework = currentConfiguration.frameworkType;
  }, [currentConfiguration]);

  useEffect(() => {
    if (testCompleted) {
      handleAllStepsCompleted(true);
    } else {
      updateStatus();
      if (failureHanderStep.current) {
        failureHanderStep.current = 0;
        firstTimeOutFailerHandler();
      }
    }
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepState, testCompleted]);

  useEffect(() => {
    return () => {
      [timerOne, timerTwo, timerThree].forEach((timer) => {
        clearTimeout(timer);
      });
    };
    //  eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function firstTimeOutFailerHandler() {
    /**
     * in 30 seconds if we don't get to step 3
     * show step 2 completed and step 3 message ->We are working to allocate resources to run your tests
     * and call the secondFailurehandler
     */
    timerOne = window.setTimeout(() => {
      if (window.quickRunData.currentStep < 3) {
        const newSteps = [...steps];
        newSteps[0].status = STEP_DONE;
        newSteps[1].status = STEP_DONE;
        newSteps[2].status = STEP_INITIATED;
        newSteps[3].status = STEP_UNINITIATED;

        newSteps[2].description =
          'We are working to allocate resources to run your tests';

        setSteps(newSteps);
        handleAmplitudeEvents(EventNames.HYP_ONBOARDING_WAIT_ERROR_QUICK_RUN, {
          waitedTime: '30sec',
          errorMessage:
            'We are working to allocate resources to run your tests',
        });
        setErrorViewed([
          'We are working to allocate resources to run your tests',
        ]);
        secondTimeOutFailerHandler();
      }
    }, WAIT_TIME_FOR_TASK_COMPLETION_FIRST);
  }
  function secondTimeOutFailerHandler() {
    /**
     * in 30 seconds if we don't get to step 3
     * change the step 3 message to Looks like it's taking longer than
     *  expected due to high demand at the moment. We appreciate your patience.
     * and call third
     */

    timerTwo = window.setTimeout(() => {
      if (window.quickRunData.currentStep < 3) {
        const newSteps = [...steps];
        newSteps[0].status = STEP_DONE;
        newSteps[1].status = STEP_DONE;
        newSteps[2].status = STEP_INITIATED;
        newSteps[3].status = STEP_UNINITIATED;

        newSteps[2].description =
          'Looks like its taking longer than expected due to high demand at the moment. We appreciate your patience.';
        setSteps(newSteps);
        handleAmplitudeEvents(EventNames.HYP_ONBOARDING_WAIT_ERROR_QUICK_RUN, {
          waitedTime: '30sec',
          errorMessage:
            'Looks like its taking longer than expected due to high demand at the moment. We appreciate your patience.',
        });
        const errors = [...errorViewed];
        errors.push(
          'Looks like its taking longer than expected due to high demand at the moment. We appreciate your patience.'
        );
        setErrorViewed(errors);
        thirdTimeOutFailerHandler();
      }
    }, WAIT_TIME_FOR_TASK_COMPLETION_SECOND);
  }
  function thirdTimeOutFailerHandler() {
    /**
     * in 2 minutes if we don't get to step 3
     * change the step 3 message to Looks like something went wrong.
     * We are working on this.
     * Meanwhile, please contact our Customer Support for assistance
     * step three status = failed
     * Allow user to go to dashboard
     */
    timerThree = window.setTimeout(() => {
      if (window.quickRunData.currentStep < 3) {
        const newSteps = [...steps];
        newSteps[0].status = STEP_DONE;
        newSteps[1].status = STEP_DONE;
        newSteps[2].status = STEP_FAILED;
        newSteps[3].status = STEP_UNINITIATED;

        newSteps[2].description = getFinalFailureMessage();

        handleAmplitudeEvents(EventNames.HYP_ONBOARDING_WAIT_ERROR_QUICK_RUN, {
          waitedTime: '2min',
          errorMessage:
            'Looks like something went wrong. We are working on this. Meanwhile, please contact our Customer Support for assistance',
        });
        setSteps(newSteps);
        setTestCompleted(true);
      }
    }, WAIT_TIME_FOR_TASK_COMPLETION_FINAL);
  }

  function updateStatus() {
    const newSteps = [...steps];
    if (window.quickRunData.currentStep === 0) {
      newSteps[0].status = STEP_INITIATED;
      newSteps[1].status = STEP_UNINITIATED;
      newSteps[2].status = STEP_UNINITIATED;
      newSteps[3].status = STEP_UNINITIATED;
    } else if (window.quickRunData.currentStep === 1) {
      newSteps[0].status = STEP_DONE;
      newSteps[1].status = STEP_INITIATED;
      newSteps[2].status = STEP_UNINITIATED;
      newSteps[3].status = STEP_UNINITIATED;
    } else if (window.quickRunData.currentStep === 2) {
      newSteps[0].status = STEP_DONE;
      newSteps[1].status = STEP_DONE;
      newSteps[2].status = STEP_INITIATED;
      newSteps[2].description = 'Discovering your Test cases';
      newSteps[3].status = STEP_UNINITIATED;
    } else if (window.quickRunData.currentStep === 3) {
      newSteps[0].status = STEP_DONE;
      newSteps[1].status = STEP_DONE;
      newSteps[2].status = STEP_DONE;
      newSteps[2].description = 'Discovering your Test cases';
      newSteps[3].status = STEP_INITIATED;
      // after getting 3rd step, we are completing 4th automaticaly showing 4th, thats why added timeout
      window.setTimeout(() => {
        newSteps[3].status = STEP_DONE;
        setSteps(newSteps);
        isCompletedRef.current = true;
        setTestCompleted(true);
        handleAmplitudeEvents(EventNames.HYP_ONBOARDING_QUICK_RUN_JOB_INVOKED, {
          errorViewed: errorViewed.length ? errorViewed : 'none',
        });
      }, 1000);
    }
    setSteps(newSteps);
  }

  return (
    <div className="test-status-steps-container">
      {steps
        .filter((step) => step.status !== STEP_UNINITIATED)
        .map((step, index) => {
          return (
            <div className="test-status-single-step" key={index}>
              <div className="step-loader-conatiner">
                {step.status === STEP_DONE ? (
                  <Icon name={step.iconName} />
                ) : step.status === STEP_FAILED ? (
                  <Icon name="onboard/crossbold" />
                ) : (
                  <div className="test-step-loading-icon">
                    <img src={LoadingIcon} alt={`step ${step.status}`} />
                  </div>
                )}
              </div>

              <Typography
                variant="subtext-1"
                color="ltw-text-gray-4"
                className="test-status-step-description"
              >
                {step.description}
              </Typography>
            </div>
          );
        })}
    </div>
  );
};

export default TestStatusSteps;
